import { useCallback } from 'react';
import { Text, Badge, Divider, Anchor, Blockquote } from '@mantine/core';
import { EditableText } from '@ayocore/platform/client/components/EditableText';
import { useNavigate } from 'react-router';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import EmailViewer from '@ayocore/mailer/client/components/EmailViewer';
import { ViewPanel, InfoRow } from '@ayocore/platform/client/components/EntityViewLayout';
import { UserPicker } from '@ayocore/platform/identity/client/components/UserPicker';
import { ReferenceSinglePicker } from '@ayocore/platform/client/components/InlinePicker';
import { LeadPartnerField } from './LeadPartnerField';
import { TagPicker } from '@ayocore/erp/crm/client/components/Tags';
import { PartnerView } from '../../../Partners/views';
import { useLeadSourceRepository, useSalesAreaRepository } from '../../../../rpc';
import type { LeadExtended } from '../LeadViewController';
import type { Partner } from '../../../../../schemas/partner.schema';

/** Тип источника: письмо (для навигации в mailer) */
const SOURCE_TYPE_EMAIL = 'mailer::IncomingEmailRepository';

interface LeadInfoProps {
  lead: LeadExtended;
  partner: Partner | null;
  canEdit?: boolean;
  onPartnerClick?: (partnerId: number) => void;
  onPartnerSave?: (partnerId: number | null) => void | Promise<void>;
  onOwnerSave?: (ownerId: number | null) => void | Promise<void>;
  onTagsSave?: (tags: string[]) => void | Promise<void>;
  onNotesSave?: (notes: string) => void | Promise<void>;
  onDescriptionSave?: (description: string) => void | Promise<void>;
  onLeadSourceSave?: (leadSourceId: number | null) => void | Promise<void>;
  onSalesAreaSave?: (salesAreaId: number | null) => void | Promise<void>;
}

const placeholderClass = 'entity-view-placeholder-dimmed';

/**
 * Блок информации о лиде: партнёр, источник, теги, валюта, ответственный, описание, примечания.
 * При canEdit — EditableText с саджестом для партнёра и ответственного,
 * EditableText для тегов (comma-separated), описания и примечаний.
 */
export function LeadInfo({
  lead,
  partner,
  canEdit = false,
  onPartnerClick,
  onPartnerSave,
  onOwnerSave,
  onTagsSave,
  onNotesSave,
  onDescriptionSave,
  onLeadSourceSave,
  onSalesAreaSave,
}: LeadInfoProps) {
  const navigate = useNavigate();
  const emailDrawer = useDrawer<{ emailUuid: string }>(EmailViewer);
  const partnerDrawer = useDrawer(PartnerView, {
    openInTab: props => navigate(`/crm/partners/${props.partnerId}`),
  });
  const leadSourceRepository = useLeadSourceRepository();
  const salesAreaRepository = useSalesAreaRepository();

  const partnerEditable = canEdit && !!onPartnerSave;
  const ownerEditable = canEdit && !!onOwnerSave;
  const tagEditable = canEdit && !!onTagsSave;
  const descriptionEditable = canEdit && !!onDescriptionSave;
  const leadSourceEditable = canEdit && !!onLeadSourceSave;
  const salesAreaEditable = canEdit && !!onSalesAreaSave;

  // Загрузчики справочников: вызываются по первому открытию пикера.
  const fetchLeadSourceOptions = useCallback(async () => {
    const rows = await leadSourceRepository
      .filterActive()
      .orderBy('sort_order', 'asc')
      .limit(100)
      .fetch();
    return rows.map(r => ({ id: r.id, label: r.name ?? `#${r.id}` }));
  }, [leadSourceRepository]);

  const fetchSalesAreaOptions = useCallback(async () => {
    const rows = await salesAreaRepository
      .filterActive()
      .orderBy('sort_order', 'asc')
      .limit(100)
      .fetch();
    return rows.map(r => ({ id: r.id, label: r.name ?? `#${r.id}` }));
  }, [salesAreaRepository]);

  const handlePartnerClick = (partnerId: number) => {
    if (onPartnerClick) {
      onPartnerClick(partnerId);
      return;
    }
    partnerDrawer.open({ partnerId, isModal: true }, {});
  };

  const hasEmailSource = lead.source_entity_type === SOURCE_TYPE_EMAIL && lead.source_entity_uuid;

  return (
    <ViewPanel>
      <InfoRow>
        <InfoRow.TitleCol>Партнер</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <LeadPartnerField
            partner={partner}
            onChange={id => void onPartnerSave?.(id)}
            canEdit={partnerEditable}
            onPartnerClick={handlePartnerClick}
            leadId={lead.id}
            leadSourceId={lead.lead_source_id ?? null}
          />
        </InfoRow.ValueCol>
      </InfoRow>

      {hasEmailSource && (
        <InfoRow>
          <InfoRow.TitleCol>Из письма</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Anchor
              size='sm'
              href='#'
              onClick={e => {
                e.preventDefault();
                emailDrawer.open({
                  emailUuid: lead.source_entity_uuid as string,
                  title: 'Письмо',
                });
              }}
            >
              Открыть письмо
            </Anchor>
          </InfoRow.ValueCol>
        </InfoRow>
      )}

      <InfoRow>
        <InfoRow.TitleCol>Ответственный</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <UserPicker
            value={lead.manager_id ?? null}
            user={lead.manager ?? null}
            onChange={id => void onOwnerSave?.(id)}
            canEdit={ownerEditable}
            placeholder='Не назначен'
          />
        </InfoRow.ValueCol>
      </InfoRow>

      <InfoRow>
        <InfoRow.TitleCol>Теги</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <TagPicker
            value={(lead.tags as string[]) ?? []}
            onChange={tags => void onTagsSave?.(tags)}
            canEdit={tagEditable}
            placeholder='Не указаны'
          />
        </InfoRow.ValueCol>
      </InfoRow>

      <InfoRow>
        <InfoRow.TitleCol>Источник</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <ReferenceSinglePicker
            value={lead.lead_source_id ?? null}
            selectedLabel={lead.lead_source?.name ?? null}
            onChange={id => void onLeadSourceSave?.(id)}
            fetchOptions={fetchLeadSourceOptions}
            canEdit={leadSourceEditable}
            placeholder='Не указан'
            emptyLabel='Не указан'
            size='sm'
            inputSize='xs'
          />
        </InfoRow.ValueCol>
      </InfoRow>

      <InfoRow>
        <InfoRow.TitleCol>Регион</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          <ReferenceSinglePicker
            value={lead.sales_area_id ?? null}
            selectedLabel={lead.sales_area?.name ?? null}
            onChange={id => void onSalesAreaSave?.(id)}
            fetchOptions={fetchSalesAreaOptions}
            canEdit={salesAreaEditable}
            placeholder='Не указан'
            emptyLabel='Не указан'
            size='sm'
            inputSize='xs'
          />
        </InfoRow.ValueCol>
      </InfoRow>

      <InfoRow>
        <InfoRow.TitleCol>Описание</InfoRow.TitleCol>
        <InfoRow.ValueCol>
          {descriptionEditable ? (
            <EditableText
              value={lead.description ?? ''}
              size='sm'
              canEdit
              placeholder='Добавить описание...'
              className={!lead.description ? placeholderClass : undefined}
              allowEmptySave
              maxRows={10}
              onSave={v => void onDescriptionSave?.(v)}
              style={{
                minWidth: 0,
                flex: 1,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            />
          ) : (
            <Text
              size='sm'
              style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {lead.description || 'Не указано'}
            </Text>
          )}
        </InfoRow.ValueCol>
      </InfoRow>

      {lead.currency && lead.currency !== 'RUB' && (
        <InfoRow>
          <InfoRow.TitleCol>Валюта</InfoRow.TitleCol>
          <InfoRow.ValueCol>
            <Text size='sm'>{lead.currency}</Text>
          </InfoRow.ValueCol>
        </InfoRow>
      )}

      {/* Причина отказа — показываем, если лид был закрыт с отказом
          (поля заполняются модалкой смены статуса при is_negative=true) */}
      {(lead.lost_reason_id != null || lead.lost_comment) && (
        <>
          <InfoRow>
            <InfoRow.TitleCol>Причина отказа</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              {lead.lost_reason?.name ? (
                <Badge size='sm' variant='light' color='red'>
                  {lead.lost_reason.name}
                </Badge>
              ) : lead.lost_reason_id != null ? (
                <Text size='sm' c='dimmed' fs='italic'>
                  Указана (#{lead.lost_reason_id})
                </Text>
              ) : (
                <Text size='sm' c='dimmed' fs='italic'>
                  Не указана
                </Text>
              )}
            </InfoRow.ValueCol>
          </InfoRow>
          {lead.lost_comment && (
            <InfoRow>
              <InfoRow.TitleCol>Комментарий к отказу</InfoRow.TitleCol>
              <InfoRow.ValueCol>
                <Text
                  size='sm'
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {lead.lost_comment}
                </Text>
              </InfoRow.ValueCol>
            </InfoRow>
          )}
        </>
      )}

      <Divider my='md' />
      <Blockquote color='gray' radius='xs' p='xs' style={{ fontSize: 12 }}>
        <EditableText
          value={lead.notes ?? ''}
          canEdit={canEdit && !!onNotesSave}
          onSave={onNotesSave ?? (() => {})}
          placeholder='Добавить примечание...'
          size='sm'
          maxRows={6}
          style={{
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap',
            display: 'block',
            verticalAlign: 'top',
            fontSize: 12,
          }}
        />
      </Blockquote>
    </ViewPanel>
  );
}
